import React, { useState, Fragment } from 'react'
import { Disclosure, Transition } from '@headlessui/react'
import { ToastContainer } from 'react-toastify'
import Cookies from 'js-cookie'
import { useConfig } from 'msp-integrations'
import { useIntl } from 'react-intl'
import { ChevronUpIcon } from '@heroicons/react/solid'
import Image from '@atoms/Image/Image'
import NewsletterForm from '@molecules/NewsletterForm/NewsletterForm'
import CustomLinkComponent from '@atoms/CustomLink/CustomLinkComponent'
import { TopNewsletterBannerProps } from './TopNewsletterBanner.types'
import { twMerge } from 'tailwind-merge'
import { isMobile } from 'msp-integrations'

const TopNewsletterBanner = ({
  data: {
    backgroundColor,
    footerText,
    headline,
    image,
    imageAlt,
    newsletterSource = 'modal',
    text,
    title
  },
  custom,
  displayNewsletterBannerByDefault = true
}: TopNewsletterBannerProps) => {
  const {
    cookiePrefix,
    theme: {
      font,
      mspComponents: { topNewsletterBanner }
    }
  } = useConfig()
  const color = topNewsletterBanner?.getBgColor(backgroundColor)
  const fontColor = topNewsletterBanner?.getColor(backgroundColor)
  const icon = topNewsletterBanner?.getIcon(backgroundColor)
  const errorColor = topNewsletterBanner?.getErrorColor(backgroundColor)

  const { formatMessage } = useIntl()
  const [showInfoBanner, setShowInfoBanner] = useState(
    displayNewsletterBannerByDefault
  )
  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false)
  const cookieName = `${cookiePrefix}Newsletter`
  const cookieValue = Cookies.get(cookieName)

  const confirmationMessage = (
    <div className={twMerge('text-16 px-8 pb-24', font.regular)}>
      {formatMessage({
        defaultMessage:
          'Thank you for your registration. You will soon receive an email to confirm your email address.',
        id: 'msp.newsletter.success_message'
      })}
    </div>
  )
  const TopInfoBannerIcons = {
    openIcon: <ChevronUpIcon className='h-5 w-5 rotate-180 transform' />,
    closeIcon: <ChevronUpIcon className='h-5 w-5' />
  }

  const setCookie = (value: boolean) => {
    if (!cookieValue) {
      const oneYearAhead = new Date()
      oneYearAhead.setFullYear(oneYearAhead.getFullYear() + 1)
      Cookies.set(cookieName, value, { expires: oneYearAhead })
    }
  }

  const scrollToSuccessfullMessage = () => {
    if (isMobile()) {
      const element = document.querySelector(
        '[data-id="newsletter-form-container-id"]'
      )
      if (element) {
        const supportsSmoothScroll =
          'scrollBehavior' in document.documentElement.style
        if (supportsSmoothScroll) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          })
        } else {
          element.scrollIntoView()
        }
      }
    }
  }

  const subscriptionSuccess = () => {
    setCookie(true)
    setShowConfirmationMessage(true)
    scrollToSuccessfullMessage()
  }

  return (
    <>
      <Transition
        as={Fragment}
        show={showInfoBanner}
        enter='ease-in-out duration-500'
        enterFrom='-translate-y-full'
        enterTo='translate-y-0'
        leave='ease-in-out duration-500'
        leaveFrom='translate-y-0'
        leaveTo='-translate-y-full'
      >
        <div
          id='top-newsletter-banner'
          className={twMerge(
            'sticky top-0 z-50 block max-h-screen w-full transform select-none overflow-y-auto pt-3 text-center font-medium transition duration-500',
            font.regular,
            color,
            fontColor
          )}
        >
          <Disclosure>
            {({ open }) => (
              <>
                <Disclosure.Button
                  className={twMerge(
                    'flex w-full flex-col items-center justify-center pb-3 sm:flex-row',
                    icon
                  )}
                >
                  <div className='flex items-center'>
                    <span
                      className={twMerge(
                        'text-12 tracking-1.2 leading-12 font-medium uppercase',
                        font.wide
                      )}
                    >
                      {headline}
                    </span>
                    {open
                      ? TopInfoBannerIcons.closeIcon
                      : TopInfoBannerIcons.openIcon}
                  </div>
                </Disclosure.Button>
                <Transition
                  show={open}
                  enter='transition duration-100 ease-out'
                  enterFrom='transform scale-95 opacity-0'
                  enterTo='transform scale-100 opacity-100'
                  leave='transition duration-75 ease-out'
                  leaveFrom='transform scale-100 opacity-100'
                  leaveTo='transform scale-95 opacity-0'
                >
                  <div className='m-auto flex flex-col justify-between gap-24 lg:flex-row lg:gap-20 xl:gap-28'>
                    <Disclosure.Panel
                      className='tracking-0.6 leading-20 relative flex h-[500px] select-text flex-col py-6 text-center font-bold lg:h-auto lg:w-1/2 lg:min-w-[600px] lg:flex-row xl:min-w-[750px]'
                      style={{
                        backgroundImage: `url('${custom?.content?.backgroundImage?.path}')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    >
                      <div className='relative mx-auto flex h-full max-w-xl flex-col gap-5 lg:static lg:mx-0 lg:flex-row lg:pl-16'>
                        <div className='min-w-96 flex flex-col justify-between gap-7 px-5 lg:w-[280px] lg:gap-0 lg:px-0 xl:w-[410px]'>
                          <div className='lg:pt-7'>
                            {title && (
                              <h3 className='headline-h3 text-left'>{title}</h3>
                            )}
                            {text && (
                              <p className='font-roboto mt-4 text-left text-xl font-light leading-7 lg:mt-3'>
                                {text}
                              </p>
                            )}
                          </div>
                          {footerText && (
                            <p className='font-roboto text-12 text-left font-normal leading-none tracking-wider'>
                              {footerText}
                            </p>
                          )}
                        </div>
                        {image && (
                          <CustomLinkComponent
                            href={custom?.content?.link || undefined}
                            noStyle
                            className='absolute -bottom-20 z-0 h-80 w-full object-cover px-5 lg:-right-14 lg:bottom-auto lg:top-6 lg:w-80 lg:px-0 lg:pb-4'
                          >
                            <Image
                              src={custom?.content?.image?.path}
                              alt={imageAlt || ''}
                              loading='eager'
                              width={image.meta_data.width.toString()}
                              height={image.meta_data.height.toString()}
                              className='h-80 w-full object-cover lg:w-80'
                            />
                          </CustomLinkComponent>
                        )}
                      </div>
                    </Disclosure.Panel>
                    <Disclosure.Panel
                      className='lg:min-h-[430px]'
                      data-id='newsletter-form-container-id'
                    >
                      {showConfirmationMessage ? (
                        confirmationMessage
                      ) : (
                        <NewsletterForm
                          id='newsletter-form-top-newsletter-banner'
                          onSuccess={subscriptionSuccess}
                          newsletterSource={newsletterSource || 'modal'}
                          className={twMerge(
                            'm-auto max-w-xl px-7 pb-6 lg:pr-16',
                            fontColor
                          )}
                          mode='light'
                          buttonClassname={twMerge(
                            'mt-9 w-56 lg:w-full bg-transparent border-white border font-medium lg:mt-11',
                            fontColor
                          )}
                          fontColor={fontColor}
                          buttonIconName='Mail'
                          additionalProps={{ errorColor: errorColor }}
                        />
                      )}
                    </Disclosure.Panel>
                  </div>
                </Transition>
              </>
            )}
          </Disclosure>
        </div>
      </Transition>
      <ToastContainer
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='dark'
      />
    </>
  )
}

export default TopNewsletterBanner
